import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable, Subject, first, takeUntil, combineLatest } from 'rxjs';

import { AddTestPermissions, ClearGlobalSearch, GlobalSearch, LoadTagProjections, SearchTagsSuccess } from '@completion/actions';
import { Permissions } from '@completion/models';
import { State } from '@completion/reducers';
import { FeatureFlagService, ProjectAccessService } from '@completion/services';
import { ContextTool, ContextToolbarService } from './context-toolbar.service';
import { getCurrentProjectName } from '@completion/selectors';
import { getGlobalSearchResponse } from '@completion/selectors';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ProjectConfigService } from '../core/services/project-config.service';
import { ProjectConfigs } from '@completion/enums';
import { CameraScannerOverlayComponent } from './camera-scanner-overlay/camera-scanner-overlay.component';
import { GlobalSearchResultDialogComponent } from './global-search-result-dialog/global-search-result-dialog.component';

@Component({
  selector: 'app-context-toolbar',
  templateUrl: './context-toolbar.component.html',
  styleUrls: ['./context-toolbar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ContextToolbarComponent implements OnInit, OnDestroy {
  tools$: Observable<ContextTool[]>;
  showTestingUtilities: boolean;
  showCameraScannerOverlay: boolean;
  areTestingToolsActive: boolean;
  testingToolStatusDictionary: Record<string, boolean> = {
    adminPermissions: false,
  };
  @Input() isOnline: boolean;
  @Input() isAdmin: boolean;

  currentProjectName$ = this.store.select(getCurrentProjectName);
  value: string;
  private globalSearchDialogRef: MatDialogRef<GlobalSearchResultDialogComponent> | null = null;

  iconClass$: BehaviorSubject<string> = new BehaviorSubject('pending');

  private readonly destroy$: Subject<void> = new Subject();
  protected displayProjectOverview$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  protected displayProjectSetup$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  constructor(
    private readonly contextToolbarService: ContextToolbarService,
    private readonly dialog: MatDialog,
    private readonly store: Store<State>,
    private readonly featureFlag: FeatureFlagService,
    private readonly router: Router,
    private readonly config: ProjectConfigService,
    private readonly access: ProjectAccessService,
  ) {
    this.tools$ = contextToolbarService.currentTools;

    if (this.featureFlag.isEnabled('testingUtils')) {
      this.showTestingUtilities = true;
    }

    if (this.featureFlag.isEnabled('cameraScannerOverlay')) {
      this.showCameraScannerOverlay = true;
    }
  }
  ngOnInit(): void {
    this.store
      .select(getGlobalSearchResponse)
      .pipe(takeUntil(this.destroy$))
      .subscribe((response) => {
        if (!response || this.globalSearchDialogRef) return;
        this.globalSearchDialogRef = this.dialog.open(GlobalSearchResultDialogComponent, {
          width: 'min(1180px, 96vw)',
          maxWidth: '98vw',
          maxHeight: '90vh',
          data: response,
        });
        this.globalSearchDialogRef
          .afterClosed()
          .pipe(first())
          .subscribe(() => {
            this.globalSearchDialogRef = null;
            this.clearSearch();
          });
      });

    // Combine latest initialization and access observables so we only
    // evaluate access logic when config is ready and access state is known.
    combineLatest([this.config.isInitialized$, this.access.isAdmin$, this.access.isVendor$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([isInitialized, isAdmin, isVendor]) => {
        if (!isInitialized) {
          return;
        }

        const displayProjectOverviewForReadOnly = this.config.getConfig(ProjectConfigs.DISPLAY_PROJECT_OVERVIEW_FOR_READ_ONLY_USER);

        if (displayProjectOverviewForReadOnly) {
          // When this config flag is enabled we want to show project overview
          // also for read-only users (i.e. non-vendors). Vendors should not see it.
          this.displayProjectOverview$.next(!isVendor);
        } else {
          // Default behaviour: only admins see the project overview.
          this.displayProjectOverview$.next(isAdmin);
        }

        // Static context button for project setup should follow admin access.
        this.displayProjectSetup$.next(isAdmin);
      });
  }

  goToProjectSetup() {
    this.router.navigate(['project-setup']);
  }

  addAdminPermissions() {
    this.store.dispatch(new AddTestPermissions([Permissions.SIGN_FOR_OTHERS]));
    this.testingToolStatusDictionary.adminPermissions = true;
    this.areTestingToolsActive = true;
  }

  search() {
    if (this.value?.trim()) {
      this.store.dispatch(new GlobalSearch(this.value.trim()));
    }
  }
  clearSearch() {
    this.value = '';
    this.store.dispatch(new SearchTagsSuccess([]));
    this.store.dispatch(new ClearGlobalSearch());
  }

  openCameraScanner() {
    // Preload tag projections so we can match OCR results locally
    this.store.dispatch(new LoadTagProjections());

    const dialogRef = this.dialog.open(CameraScannerOverlayComponent, {
      width: '100vw',
      height: '100vh',
      maxWidth: '100vw',
      maxHeight: '100vh',
      panelClass: 'camera-scanner-dialog',
    });

    dialogRef
      .afterClosed()
      .pipe(first())
      .subscribe((matchedTags: string[] | null) => {
        if (matchedTags && matchedTags.length > 0) {
          this.value = matchedTags.join(', ');
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  protected goToProjectOverview() {
    this.router.navigate(['project-overview']);
  }
}
